import { notificationError, notificationSuccess } from "~/utils/notifications";

export interface ItemAvailability {
  status: "in_stock" | "production" | "shortage";
  from_stock: number;
  to_produce: number;
  production_days: number;
}

export interface CartItem {
  id: number;
  product_id: number;
  color_id: number | null;
  quantity: number;
  product_name: string;
  product_alias: string;
  price: number | null;
  size_sm: string | null;
  vendor_code: number | null;
  disabled: boolean;
  /** Заполнены ли вес и габариты: без них доставку не рассчитать */
  orderable: boolean;
  category_name: string;
  category_alias: string;
  color_name: string | null;
  color_alias: string | null;
  color_bg: string | null;
  image_id: number | null;
  image_color_alias: string | null;
  stock_qty: number;
  production_days: number | null;
  availability: ItemAvailability;
  /** Цвета товара с остатками — для смены цвета прямо в корзине */
  colors: { color_id: number; name: string; alias: string; bg_color: string; qty: number }[] | null;
}

export interface CartPromo {
  code: string;
  kind: "percent" | "fixed";
  value: number;
  min_goods_total: number | null;
  ends_at: string;
  /** Разделы, на которые действует промокод; null — на все */
  category_names: string[] | null;
  discount: number;
  /** Почему скидки сейчас нет: срок истёк, не набрана сумма и т. п. */
  message: string | null;
}

export interface CartState {
  items: CartItem[];
  count: number;
  /** Сумма товаров до скидки */
  total: number;
  /** Скидка по промокоду — только на товары */
  discount?: number;
  promo?: CartPromo | null;
  /** Срок готовности всего заказа: максимум по позициям, требующим изготовления */
  production_days: number;
  /** Есть позиция, которой на складе меньше, чем заказано, и она не изготавливается */
  has_shortage: boolean;
}

const emptyCart = (): CartState => ({
  items: [],
  count: 0,
  total: 0,
  discount: 0,
  promo: null,
  production_days: 0,
  has_shortage: false,
});

export const useCart = () => {
  // Общий стейт в стиле useAuthUser — Pinia в проекте не используется
  const cart = useState<CartState>("cart", emptyCart);
  const pending = useState<boolean>("cart-pending", () => false);
  const shopAccess = useShopAccess();

  const apply = (data: CartState | null | undefined) => {
    cart.value = data && Array.isArray(data.items) ? data : emptyCart();
  };

  // Мутации возвращают полное состояние корзины, поэтому второй запрос не нужен
  const request = async (
    url: string,
    options: Record<string, unknown>,
    errorTitle: string
  ) => {
    if (!shopAccess.value) return false;
    pending.value = true;
    try {
      apply(await $fetch<CartState>(url, options));
      return true;
    } catch (error: any) {
      notificationError(error?.data?.message ?? "Не удалось выполнить действие", errorTitle);
      return false;
    } finally {
      pending.value = false;
    }
  };

  const refresh = async () => {
    if (!shopAccess.value) {
      cart.value = emptyCart();
      return;
    }
    try {
      apply(
        await $fetch<CartState>("/api/cart", {
          headers: useRequestHeaders(["cookie"]) as HeadersInit,
        })
      );
    } catch {
      // Молча: корзина не должна ломать страницу, если она недоступна
      cart.value = emptyCart();
    }
  };

  const add = async (productId: number, colorId: number | null = null, quantity = 1) => {
    const ok = await request(
      "/api/cart/item",
      { method: "POST", body: { product_id: productId, color_id: colorId, quantity } },
      "Не удалось добавить в корзину"
    );
    if (ok) notificationSuccess("Товар добавлен в корзину");
    return ok;
  };

  const update = (itemId: number, quantity: number) =>
    request(
      "/api/cart/item",
      { method: "PUT", body: { id: itemId, quantity } },
      "Не удалось изменить количество"
    );

  const remove = (itemId: number) =>
    request(
      "/api/cart/item",
      { method: "DELETE", query: { id: itemId } },
      "Не удалось удалить позицию"
    );

  const clear = () =>
    request("/api/cart", { method: "DELETE" }, "Не удалось очистить корзину");

  const setColor = (id: number, colorId: number) =>
    request(
      "/api/cart/color",
      { method: "PUT", body: { id, color_id: colorId } },
      "Не удалось сменить цвет"
    );

  // Ошибку промокода показываем под полем ввода, а не всплывающим окном,
  // поэтому возвращаем её текст (null — применился)
  const applyPromo = async (code: string): Promise<string | null> => {
    if (!shopAccess.value) return "Корзина недоступна";
    pending.value = true;
    try {
      apply(await $fetch<CartState>("/api/cart/promo", { method: "POST", body: { code } }));
      return null;
    } catch (error: any) {
      return error?.data?.message ?? "Не удалось применить промокод";
    } finally {
      pending.value = false;
    }
  };

  const removePromo = () =>
    request("/api/cart/promo", { method: "DELETE" }, "Не удалось убрать промокод");

  return {
    cart,
    pending,
    items: computed(() => cart.value.items),
    count: computed(() => cart.value.count),
    total: computed(() => cart.value.total),
    discount: computed(() => cart.value.discount ?? 0),
    promo: computed(() => cart.value.promo ?? null),
    productionDays: computed(() => cart.value.production_days ?? 0),
    hasShortage: computed(() => !!cart.value.has_shortage),
    isEmpty: computed(() => cart.value.items.length === 0),
    refresh,
    add,
    update,
    remove,
    clear,
    setColor,
    applyPromo,
    removePromo,
  };
};
